import Link from "next/link"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination"
import { Button, buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  ChevronFirstIcon,
  ChevronLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { buildListingHref, paginationRange } from "@/lib/marketing/pagination"

interface ListingPaginationProps {
  /** Base path of the listing, e.g. "/blog". */
  basePath: string
  /** Current page (1-based). */
  page: number
  /** Total number of pages. */
  totalPages: number
  /** Active category to preserve across page links (optional). */
  category?: string
  /** Active search query to preserve across page links (optional). */
  q?: string
  /** Show first/last controls and a direct page jump form. */
  showPageJump?: boolean
}

/**
 * Server-rendered, SEO-friendly pagination for listing pages. Each control is a
 * real `?page=` link (category preserved), so every page is crawlable and
 * shareable. Renders nothing when there's only one page.
 */
export function ListingPagination({
  basePath,
  page,
  totalPages,
  category,
  q,
  showPageJump = false,
}: ListingPaginationProps) {
  if (totalPages <= 1) return null

  const href = (p: number) =>
    buildListingHref(basePath, { category, q, page: p })
  const tokens = paginationRange(page, totalPages)
  const hasPrev = page > 1
  const hasNext = page < totalPages

  return (
    <Pagination className="mt-12 flex-col gap-4 sm:flex-row">
      <PaginationContent className="flex-wrap justify-center">
        {showPageJump && (
          <PaginationItem>
            {hasPrev ? (
              <Link
                href={href(1)}
                aria-label="Go to first page"
                className={cn(buttonVariants({ variant: "ghost", size: "default" }), "px-2!")}
              >
                <ChevronFirstIcon />
                <span className="hidden lg:block">First</span>
              </Link>
            ) : (
              <span
                aria-disabled
                aria-label="First page"
                className={cn(
                  buttonVariants({ variant: "ghost", size: "default" }),
                  "pointer-events-none px-2! opacity-50",
                )}
              >
                <ChevronFirstIcon />
                <span className="hidden lg:block">First</span>
              </span>
            )}
          </PaginationItem>
        )}

        {/* Previous */}
        <PaginationItem>
          {hasPrev ? (
            <Link
              href={href(page - 1)}
              aria-label="Go to previous page"
              rel="prev"
              className={cn(buttonVariants({ variant: "ghost", size: "default" }), "pl-2!")}
            >
              <ChevronLeftIcon data-icon="inline-start" />
              <span className="hidden sm:block">Previous</span>
            </Link>
          ) : (
            <span
              aria-disabled
              className={cn(
                buttonVariants({ variant: "ghost", size: "default" }),
                "pointer-events-none pl-2! opacity-50",
              )}
            >
              <ChevronLeftIcon data-icon="inline-start" />
              <span className="hidden sm:block">Previous</span>
            </span>
          )}
        </PaginationItem>

        {/* Page numbers */}
        {tokens.map((token, i) =>
          token === "ellipsis" ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={token}>
              <Link
                href={href(token)}
                aria-current={token === page ? "page" : undefined}
                className={cn(
                  buttonVariants({
                    variant: token === page ? "outline" : "ghost",
                    size: "icon",
                  }),
                )}
              >
                {token}
              </Link>
            </PaginationItem>
          ),
        )}

        {/* Next */}
        <PaginationItem>
          {hasNext ? (
            <Link
              href={href(page + 1)}
              aria-label="Go to next page"
              rel="next"
              className={cn(buttonVariants({ variant: "ghost", size: "default" }), "pr-2!")}
            >
              <span className="hidden sm:block">Next</span>
              <ChevronRightIcon data-icon="inline-end" />
            </Link>
          ) : (
            <span
              aria-disabled
              className={cn(
                buttonVariants({ variant: "ghost", size: "default" }),
                "pointer-events-none pr-2! opacity-50",
              )}
            >
              <span className="hidden sm:block">Next</span>
              <ChevronRightIcon data-icon="inline-end" />
            </span>
          )}
        </PaginationItem>

        {showPageJump && (
          <PaginationItem>
            {hasNext ? (
              <Link
                href={href(totalPages)}
                aria-label="Go to last page"
                className={cn(buttonVariants({ variant: "ghost", size: "default" }), "px-2!")}
              >
                <span className="hidden lg:block">Last</span>
                <ChevronLastIcon />
              </Link>
            ) : (
              <span
                aria-disabled
                aria-label="Last page"
                className={cn(
                  buttonVariants({ variant: "ghost", size: "default" }),
                  "pointer-events-none px-2! opacity-50",
                )}
              >
                <span className="hidden lg:block">Last</span>
                <ChevronLastIcon />
              </span>
            )}
          </PaginationItem>
        )}
      </PaginationContent>

      {showPageJump && (
        <form action={basePath} method="get" className="flex items-center gap-2 text-sm">
          {category && category !== "all" && (
            <input type="hidden" name="category" value={category} />
          )}
          {q && <input type="hidden" name="q" value={q} />}
          <label htmlFor="pagination-page" className="whitespace-nowrap text-muted-foreground">
            Go to page
          </label>
          <Input
            key={page}
            id="pagination-page"
            name="page"
            type="number"
            min={1}
            max={totalPages}
            defaultValue={page}
            inputMode="numeric"
            aria-label={`Page number, 1 to ${totalPages}`}
            className="h-9 w-20"
          />
          <Button type="submit" variant="outline" size="sm">
            Go
          </Button>
          <span className="whitespace-nowrap text-muted-foreground">of {totalPages}</span>
        </form>
      )}
    </Pagination>
  )
}
